// src/pages/ReportsAlumni.tsx
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";
import { useTranslation } from "react-i18next";
import {
  FaCheck,
  FaMagnifyingGlass,
  FaPlus,
  FaUserGraduate,
  FaXmark,
} from "react-icons/fa6";

import http from "../api/apiClient";
import { useCan } from "../authz/useCan";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../components/lds/LdsTable";
import PageTemplate from "../components/PageTemplate";
import Spinner from "../components/Spinner";
import SlidePageTransition from "../components/transitions/SlidePageTransition";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";

type EntityId = string | number;

type AlumniPerson = {
  id?: EntityId;
  name?: string | null;
  fullName?: string | null;
  email?: string | null;
  dateBirth?: string | null;
  dateOfBirth?: string | null;
  allowStore?: boolean | null;
};

type AlumniPreferences = {
  allowStore?: boolean | null;
  newsletter?: boolean | null;
  alumni?: boolean | null;
  activities?: boolean | null;
  name?: string | null;
  email?: string | null;
};

type AlumniRecord = {
  id?: EntityId;
  parentId?: EntityId;
  childId?: EntityId;
  child?: AlumniPerson | null;
  parent?: AlumniPerson | null;
  parents?: AlumniPreferences | null;
  parentPreferences?: AlumniPreferences | null;
  childName?: string | null;
  parentName?: string | null;
  childEmail?: string | null;
  parentEmail?: string | null;
  childDateBirth?: string | null;
  childAllowStore?: boolean | null;
  newsletter?: boolean | null;
  alumni?: boolean | null;
  activities?: boolean | null;
  lifecycleOnly?: boolean;
};

type AlumniEditor = {
  id: EntityId | "";
  isNew: boolean;
  lifecycleOnly: boolean;
  parentId: EntityId | "";
  childId: EntityId | "";
  child: {
    allowStore: boolean;
    name: string;
    dateBirth: string;
    email: string;
  };
  parents: {
    allowStore: boolean;
    newsletter: boolean;
    alumni: boolean;
    activities: boolean;
    name: string;
    email: string;
  };
};

type UnknownRecord = Record<string, unknown>;

type WithdrawnChild = {
  id?: EntityId;
  firstName?: string | null;
  lastName?: string | null;
  dateBirth?: string | null;
  withdrawnAt?: string | null;
};

const normalizeList = (data: unknown): AlumniRecord[] => {
  if (Array.isArray(data)) return data as AlumniRecord[];

  if (data && typeof data === "object") {
    const record = data as UnknownRecord;

    for (const key of ["content", "items", "records"]) {
      const value = record[key];
      if (Array.isArray(value)) return value as AlumniRecord[];
    }
  }

  return [];
};

const buildName = (
  parts: Array<string | null | undefined> = []
): string => parts.filter(Boolean).join(" ").trim();

export const mergeWithdrawnChildrenWithPreferences = (
  preferences: AlumniRecord[],
  withdrawnChildren: WithdrawnChild[]
): AlumniRecord[] => {
  const childIdsWithPreferences = new Set(
    preferences
      .map((item) => item.childId ?? item.child?.id)
      .filter((id): id is EntityId => id !== null && id !== undefined)
      .map(String)
  );

  const childrenWithoutPreferences = withdrawnChildren
    .filter((child) => child.id !== null && child.id !== undefined)
    .filter((child) => !childIdsWithPreferences.has(String(child.id)))
    .map((child) => ({
      childId: child.id,
      childName: buildName([child.firstName, child.lastName]),
      childDateBirth: child.dateBirth ?? null,
      child: {
        id: child.id,
        name: buildName([child.firstName, child.lastName]),
        dateBirth: child.dateBirth ?? null,
        allowStore: true,
      },
      lifecycleOnly: true,
    }));

  return [...preferences, ...childrenWithoutPreferences];
};

const getErrorMessage = (
  error: unknown,
  t: ReturnType<typeof useTranslation>["t"]
): string => {
  const mapped = mapHttpErrorToMessage(error, t);

  if (typeof mapped === "string") return mapped;

  if (mapped && typeof mapped === "object") {
    const value = mapped as { message?: unknown; title?: unknown };

    if (typeof value.message === "string") return value.message;
    if (typeof value.title === "string") return value.title;
  }

  if (error instanceof Error) return error.message;

  return t("common.error", {
    defaultValue: "Something went wrong.",
  });
};

const getParentPreferences = (
  item: AlumniRecord
): AlumniPreferences =>
  item.parents ??
  item.parentPreferences ?? {
    newsletter: item.newsletter,
    alumni: item.alumni,
    activities: item.activities,
  };

const PreferencePill = ({
  enabled,
  label,
}: {
  enabled: boolean;
  label: string;
}) => (
  <span
    aria-label={`${label}: ${enabled ? "yes" : "no"}`}
    className={[
      "inline-flex min-w-[5.5rem] items-center justify-center gap-1.5 rounded-full",
      "border px-2.5 py-1 text-xs font-semibold",
      enabled
        ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-950/30 dark:text-emerald-200"
        : "border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-300",
    ].join(" ")}
  >
    {enabled ? (
      <FaCheck aria-hidden="true" className="h-3 w-3" />
    ) : (
      <FaXmark aria-hidden="true" className="h-3 w-3" />
    )}
    {label}
  </span>
);

const ReportsAlumni = () => {
  const { t } = useTranslation();

  const canReadAlumniDirect = useCan("alumni", "read");
  const canCrudAlumni = useCan("alumni", "crud");
  const canListChildren = useCan("family.children", "list");
  const canDeleteParents = useCan("family.parents", "delete");
  const canDeleteChildren = useCan("family.children", "delete");
  const canDeleteFamilies = useCan("families", "delete");

  const canReadAlumni =
    canReadAlumniDirect || canCrudAlumni;
  const canManageAlumni = canCrudAlumni;
  const canDelete =
    canManageAlumni ||
    canDeleteParents ||
    canDeleteChildren ||
    canDeleteFamilies;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<AlumniRecord[]>([]);
  const [query, setQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSaving, setModalSaving] = useState(false);
  const [editItem, setEditItem] = useState<AlumniEditor | null>(null);
  const [showChildSection, setShowChildSection] = useState(false);

  const load = useCallback(async () => {
    if (!canReadAlumni) {
      setError(
        t("error.403.message", {
          defaultValue: "You do not have access to this page.",
        })
      );
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [preferencesResponse, childrenResponse] = await Promise.all([
        http.get("/alumni/all"),
        canListChildren
          ? http.get("/children", { params: { status: "alumni" } }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      setItems(
        mergeWithdrawnChildrenWithPreferences(
          normalizeList(preferencesResponse?.data),
          normalizeList(childrenResponse?.data) as WithdrawnChild[]
        )
      );
    } catch (loadError: unknown) {
      setError(getErrorMessage(loadError, t));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [canListChildren, canReadAlumni, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();

    if (!normalizedQuery) return items;

    return items.filter((item) => {
      const childName = buildName([
        item.child?.name,
        item.child?.fullName,
        item.childName,
      ]).toLocaleLowerCase();

      const parentName = buildName([
        item.parent?.name,
        item.parent?.fullName,
        item.parentName,
      ]).toLocaleLowerCase();

      const childEmail = (
        item.child?.email ??
        item.childEmail ??
        ""
      ).toLocaleLowerCase();

      const parentEmail = (
        item.parent?.email ??
        item.parentEmail ??
        ""
      ).toLocaleLowerCase();

      return (
        childName.includes(normalizedQuery) ||
        parentName.includes(normalizedQuery) ||
        childEmail.includes(normalizedQuery) ||
        parentEmail.includes(normalizedQuery)
      );
    });
  }, [items, query]);

  const openModal = (item: AlumniRecord) => {
    const hasChildData = Boolean(
      item.childId ||
        item.child?.name ||
        item.childName ||
        item.childEmail ||
        item.child?.email ||
        item.childDateBirth ||
        item.child?.dateBirth
    );

    setEditItem({
      id: item.id ?? "",
      isNew: false,
      lifecycleOnly: Boolean(item.lifecycleOnly),
      parentId: item.parentId ?? item.parent?.id ?? "",
      childId: item.childId ?? item.child?.id ?? "",
      child: {
        allowStore:
          item.child?.allowStore ??
          item.childAllowStore ??
          hasChildData,
        name: buildName([
          item.child?.name,
          item.child?.fullName,
          item.childName,
        ]),
        dateBirth:
          item.child?.dateBirth ??
          item.child?.dateOfBirth ??
          item.childDateBirth ??
          "",
        email:
          item.child?.email ??
          item.childEmail ??
          "",
      },
      parents: {
        allowStore: true,
        newsletter:
          item.parents?.newsletter ??
          item.parentPreferences?.newsletter ??
          item.newsletter ??
          false,
        alumni:
          item.parents?.alumni ??
          item.parentPreferences?.alumni ??
          item.alumni ??
          false,
        activities:
          item.parents?.activities ??
          item.parentPreferences?.activities ??
          item.activities ??
          false,
        name: buildName([
          item.parent?.name,
          item.parent?.fullName,
          item.parentName,
        ]),
        email:
          item.parent?.email ??
          item.parentEmail ??
          "",
      },
    });

    setModalError(null);
    setShowChildSection(hasChildData);
    setModalOpen(true);
  };

  const openNewModal = () => {
    setEditItem({
      id: "",
      isNew: true,
      lifecycleOnly: false,
      parentId: "",
      childId: "",
      child: {
        allowStore: true,
        name: "",
        dateBirth: "",
        email: "",
      },
      parents: {
        allowStore: true,
        newsletter: false,
        alumni: true,
        activities: false,
        name: "",
        email: "",
      },
    });

    setShowChildSection(true);
    setModalError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (modalSaving) return;

    setModalOpen(false);
    setEditItem(null);
    setModalError(null);
  };

  const updateChildField = (
    field: keyof AlumniEditor["child"],
    value: string | boolean
  ) => {
    setEditItem((previous) =>
      previous
        ? {
            ...previous,
            child: {
              ...previous.child,
              [field]: value,
            },
          }
        : previous
    );
  };

  const updateParentField = (
    field: keyof AlumniEditor["parents"],
    value: string | boolean
  ) => {
    setEditItem((previous) =>
      previous
        ? {
            ...previous,
            parents: {
              ...previous.parents,
              [field]: value,
            },
          }
        : previous
    );
  };

  const handleSave = async () => {
    if (!canManageAlumni || !editItem) return;

    setModalSaving(true);
    setModalError(null);

    const payload = {
      childId:
        showChildSection && editItem.childId
          ? editItem.childId
          : null,
      parentId: editItem.parentId || null,
      child: showChildSection
        ? {
            allowStore: editItem.child.allowStore,
            name: editItem.child.name || null,
            dateBirth: editItem.child.dateBirth || null,
            email: editItem.child.email || null,
          }
        : {
            allowStore: false,
            name: null,
            dateBirth: null,
            email: null,
          },
      parents: {
        allowStore: true,
        newsletter: editItem.parents.newsletter,
        alumni: editItem.parents.alumni,
        activities: editItem.parents.activities,
        name: editItem.parents.name || null,
        email: editItem.parents.email || null,
      },
    };

    try {
      if (editItem.isNew) {
        await http.post("/alumni", payload);
      } else {
        await http.put("/alumni", payload);
      }

      await load();
      closeModal();
    } catch (saveError: unknown) {
      setModalError(getErrorMessage(saveError, t));
    } finally {
      setModalSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !editItem) return;

    setModalSaving(true);
    setModalError(null);

    try {
      if (editItem.id) {
        await http.delete("/alumni", {
          params: { id: editItem.id },
        });
      } else if (editItem.parentId) {
        await http.delete("/alumni", {
          params: {
            parentId: editItem.parentId,
            childId: editItem.childId || undefined,
          },
        });
      } else {
        setModalError(
          t("parentPortal.alumni.error_missing_parent", {
            defaultValue: "A parent is required.",
          })
        );
        return;
      }

      await load();
      closeModal();
    } catch (deleteError: unknown) {
      const status =
        typeof deleteError === "object" &&
        deleteError !== null &&
        "response" in deleteError
          ? (
              deleteError as {
                response?: { status?: number };
              }
            ).response?.status
          : undefined;

      if (status === 403) {
        setModalError(
          t("reports.alumni.delete_forbidden", {
            defaultValue:
              "You do not have permission to delete this preference.",
          })
        );
      } else {
        setModalError(getErrorMessage(deleteError, t));
      }
    } finally {
      setModalSaving(false);
    }
  };

  if (loading) {
    return (
      <PageTemplate title={null} wrapperClassName="max-w-7xl">
        <div className="mx-auto max-w-7xl rounded-[32px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_26px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/85 sm:p-6">
          <SlidePageTransition className="flex min-h-[18rem] items-center justify-center">
            <Spinner />
          </SlidePageTransition>
        </div>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate title={null} wrapperClassName="max-w-7xl">
      <style>{`
        @keyframes lds-page-enter {
          from {
            opacity: 0;
            transform: translateY(14px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes lds-section-enter {
          from {
            opacity: 0;
            transform: translateY(18px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .lds-page-enter {
          animation: lds-page-enter 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .lds-section-enter {
          animation: lds-section-enter 480ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        @media (prefers-reduced-motion: reduce) {
          .lds-page-enter,
          .lds-section-enter {
            animation: none;
          }
        }
      `}</style>

      <div className="lds-page-enter mx-auto max-w-7xl rounded-[32px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_26px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/85 sm:p-6">
        <SlidePageTransition className="space-y-5">
          <section className="lds-section-enter relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
            <div className="relative z-10 flex min-w-0 flex-col gap-5 lg:flex-row lg:items-center">
              <div className="flex min-w-0 flex-1 items-center gap-5">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-violet-200 to-sky-50 text-3xl text-violet-700 shadow-lg dark:border-slate-900 dark:from-violet-800 dark:to-sky-950 dark:text-violet-100 sm:h-24 sm:w-24">
                  <FaUserGraduate aria-hidden="true" />
                </div>

                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                    {t("reports.alumni.section_label", { defaultValue: "Alumni administration" })}
                  </p>
                  <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                    {t("reports.alumni.card_title", {
                      defaultValue: "Alumni preferences",
                    })}
                  </h1>
                  <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                    {t("reports.alumni.overview_description", {
                      defaultValue:
                        "Manage alumni contact details and communication preferences.",
                    })}
                  </p>
                </div>
              </div>

              <div className="flex shrink-0 flex-wrap items-center gap-3 lg:mr-36">
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
                  {t("reports.alumni.record_count", {
                    defaultValue: "{{count}} records",
                    count: filtered.length,
                  })}
                </span>

                {canManageAlumni ? (
                  <button
                    type="button"
                    onClick={openNewModal}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-5 py-3 text-sm font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200 dark:hover:bg-indigo-400/20"
                  >
                    <FaPlus aria-hidden="true" />
                    {t("reports.alumni.add_new", {
                      defaultValue: "Add alumnus",
                    })}
                  </button>
                ) : null}
              </div>
            </div>

            <div
              className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex"
              aria-hidden="true"
            >
              <FaUserGraduate className="text-[110px] text-violet-200/70 dark:text-violet-800/40" />
            </div>
          </section>

          {error ? (
            <div
              role="alert"
              className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-950/30 dark:text-red-200"
            >
              {error}
            </div>
          ) : null}

          <section className="lds-section-enter rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <label className="relative flex-1">
                <span className="sr-only">
                  {t("reports.alumni.search_placeholder", {
                    defaultValue: "Search by child, parent or email...",
                  })}
                </span>
              <FaMagnifyingGlass
                aria-hidden="true"
                className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
              />
              <input
                type="search"
                value={query}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setQuery(event.target.value)
                }
                placeholder={t(
                  "reports.alumni.search_placeholder",
                  {
                    defaultValue:
                      "Search by child, parent or email...",
                  }
                )}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-10 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 dark:border-white/10 dark:bg-slate-950/50 dark:text-white dark:focus:border-indigo-400 dark:focus:ring-indigo-400/10"
              />

              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label={t("common.actions.clear", {
                    defaultValue: "Clear search",
                  })}
                  className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-white"
                >
                  <FaXmark aria-hidden="true" className="h-4 w-4" />
                </button>
              ) : null}
              </label>
            </div>
          </section>

          <section className="lds-section-enter overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/80">
            <div className="flex flex-col gap-2 border-b border-slate-200 px-5 py-5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("reports.alumni.overview_title", {
                    defaultValue: "Alumni overview",
                  })}
                </p>
                <h2 className="mt-1 text-xl font-bold text-slate-950 dark:text-white">
                  {t("reports.alumni.list_title", { defaultValue: "Preference overview" })}
                </h2>
              </div>
              <span className="inline-flex w-fit items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                {t("common.result_count", {
                  count: filtered.length,
                  defaultValue: "{{count}} results",
                })}
              </span>
            </div>

            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-[22px] bg-slate-100 text-2xl text-slate-400 dark:bg-white/5 dark:text-slate-500">
                  <FaUserGraduate aria-hidden="true" />
                </span>
                <h3 className="mt-5 text-lg font-bold text-slate-950 dark:text-white">
                  {t("reports.alumni.no_records", {
                    defaultValue:
                      "No alumni preferences were found.",
                  })}
                </h3>
              </div>
            ) : (
              <>
                <LdsTableCard className="hidden rounded-none border-0 shadow-none md:block">
                  <LdsTableScroll>
                    <LdsTable className="min-w-[980px]">
                      <LdsTableHead>
                        <LdsTableRow>
                          {[
                            t("reports.alumni.child", {
                              defaultValue: "Child",
                            }),
                            t("reports.alumni.parent_contact", {
                              defaultValue: "Parent contact",
                            }),
                            t("reports.alumni.newsletter", {
                              defaultValue: "Newsletter",
                            }),
                            t("reports.alumni.alumni_updates", {
                              defaultValue: "Alumni updates",
                            }),
                            t("reports.alumni.activities", {
                              defaultValue: "Activities",
                            }),
                            t("common.actions.actions", {
                              defaultValue: "Actions",
                            }),
                          ].map((heading, index) => (
                            <LdsTableHeadCell
                              key={heading}
                              scope="col"
                              className={index === 5 ? "text-right" : ""}
                            >
                              {heading}
                            </LdsTableHeadCell>
                          ))}
                        </LdsTableRow>
                      </LdsTableHead>

                      <LdsTableBody className="[&>tr]:animate-[lds-section-enter_360ms_cubic-bezier(0.22,1,0.36,1)_both]">
                        {filtered.map((item, index) => {
                          const childName =
                            buildName([
                              item.child?.name,
                              item.child?.fullName,
                              item.childName,
                            ]) ||
                            String(item.childId ?? "—");

                          const parentName =
                            buildName([
                              item.parent?.name,
                              item.parent?.fullName,
                              item.parentName,
                            ]) || "—";

                          const parentEmail =
                            item.parent?.email ??
                            item.parentEmail ??
                            "";

                          const preferences =
                            getParentPreferences(item);

                          const yesLabel = t("common.yes", {
                            defaultValue: "Yes",
                          });
                          const noLabel = t("common.no", {
                            defaultValue: "No",
                          });

                          return (
                            <LdsTableRow
                              key={
                                item.id ??
                                `${item.parentId ?? "parent"}-${item.childId ?? index}`
                              }
                            >
                              <LdsTableCell className="font-semibold text-slate-950 dark:text-white">
                                {childName}
                              </LdsTableCell>

                              <LdsTableCell>
                                <div className="font-medium text-slate-800 dark:text-slate-100">
                                  {parentName}
                                </div>
                                {parentEmail ? (
                                  <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                                    {parentEmail}
                                  </div>
                                ) : null}
                              </LdsTableCell>

                              {item.lifecycleOnly ? (
                                <LdsTableCell colSpan={3}>
                                  <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200">
                                    {t("reports.alumni.no_preferences", {
                                      defaultValue: "No preferences recorded",
                                    })}
                                  </span>
                                </LdsTableCell>
                              ) : (
                                <>
                                  <LdsTableCell>
                                    <PreferencePill enabled={Boolean(preferences.newsletter)} label={preferences.newsletter ? yesLabel : noLabel} />
                                  </LdsTableCell>
                                  <LdsTableCell>
                                    <PreferencePill enabled={Boolean(preferences.alumni)} label={preferences.alumni ? yesLabel : noLabel} />
                                  </LdsTableCell>
                                  <LdsTableCell>
                                    <PreferencePill enabled={Boolean(preferences.activities)} label={preferences.activities ? yesLabel : noLabel} />
                                  </LdsTableCell>
                                </>
                              )}

                              <LdsTableCell className="text-right">
                                <button
                                  type="button"
                                  onClick={() => openModal(item)}
                                  className="inline-flex min-h-9 items-center justify-center rounded-full border border-indigo-200 bg-white px-3 py-1.5 text-xs font-bold text-indigo-600 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-indigo-400/25 dark:bg-white/5 dark:text-indigo-200"
                                >
                                  {item.lifecycleOnly
                                    ? t("reports.alumni.add_preferences", {
                                        defaultValue: "Add preferences",
                                      })
                                    : t("common.actions.edit", {
                                        defaultValue: "Edit",
                                      })}
                                </button>
                              </LdsTableCell>
                            </LdsTableRow>
                          );
                        })}
                      </LdsTableBody>
                    </LdsTable>
                  </LdsTableScroll>
                </LdsTableCard>

                <div className="space-y-3 p-4 md:hidden">
                  {filtered.map((item, index) => {
                    const childName =
                      buildName([
                        item.child?.name,
                        item.child?.fullName,
                        item.childName,
                      ]) ||
                      String(item.childId ?? "—");

                    const parentName =
                      buildName([
                        item.parent?.name,
                        item.parent?.fullName,
                        item.parentName,
                      ]) || "—";

                    const parentEmail =
                      item.parent?.email ??
                      item.parentEmail ??
                      "";

                    const preferences =
                      getParentPreferences(item);

                    return (
                      <button
                        key={
                          item.id ??
                          `${item.parentId ?? "parent"}-${item.childId ?? index}`
                        }
                        type="button"
                        onClick={() => openModal(item)}
                        className="group w-full rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:bg-gradient-to-r hover:from-white hover:to-indigo-50 hover:shadow-[0_15px_35px_-15px_rgba(79,70,229,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-white/10 dark:bg-slate-950/40 dark:hover:from-slate-950 dark:hover:to-indigo-950/30"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate font-bold text-slate-950 dark:text-white">
                              {childName}
                            </div>
                            <div className="mt-1 truncate text-sm text-slate-600 dark:text-slate-300">
                              {parentName}
                            </div>
                            {parentEmail ? (
                              <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                                {parentEmail}
                              </div>
                            ) : null}
                          </div>

                          <span className="shrink-0 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 dark:border-indigo-400/25 dark:bg-indigo-400/10 dark:text-indigo-200">
                            {item.lifecycleOnly
                              ? t("reports.alumni.add_preferences", {
                                  defaultValue: "Add preferences",
                                })
                              : t("common.actions.edit", {
                                  defaultValue: "Edit",
                                })}
                          </span>
                        </div>

                        {item.lifecycleOnly ? (
                          <span className="mt-4 inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200">
                            {t("reports.alumni.no_preferences", {
                              defaultValue: "No preferences recorded",
                            })}
                          </span>
                        ) : (
                          <div className="mt-4 flex flex-wrap gap-2">
                            <PreferencePill enabled={Boolean(preferences.newsletter)} label={t("reports.alumni.newsletter", { defaultValue: "Newsletter" })} />
                            <PreferencePill enabled={Boolean(preferences.alumni)} label={t("reports.alumni.alumni_updates", { defaultValue: "Alumni" })} />
                            <PreferencePill enabled={Boolean(preferences.activities)} label={t("reports.alumni.activities", { defaultValue: "Activities" })} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        </SlidePageTransition>
      </div>

      {modalOpen && editItem ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/55 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeModal();
            }
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="alumni-dialog-title"
            className="max-h-[92vh] w-full overflow-y-auto rounded-t-[28px] border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900 sm:max-w-3xl sm:rounded-[28px]"
          >
            <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur dark:border-slate-700 dark:bg-slate-900/95 sm:px-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-indigo-600 dark:text-indigo-300">
                  {t("reports.alumni.dialog_eyebrow", {
                    defaultValue: "Alumni",
                  })}
                </p>
                <h2
                  id="alumni-dialog-title"
                  className="mt-1 text-xl font-bold text-slate-950 dark:text-white"
                >
                  {editItem.isNew
                    ? t("reports.alumni.add_title", {
                        defaultValue:
                          "Add alumni preference",
                      })
                    : t("reports.alumni.edit_title", {
                        defaultValue:
                          "Edit alumni preference",
                      })}
                </h2>
              </div>

              <button
                type="button"
                onClick={closeModal}
                disabled={modalSaving}
                aria-label={t("common.actions.close", {
                  defaultValue: "Close",
                })}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:text-slate-900 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:text-white"
              >
                <FaXmark aria-hidden="true" className="h-4 w-4" />
              </button>
            </header>

            <div className="space-y-6 p-5 sm:p-6">
              {modalError ? (
                <div
                  role="alert"
                  className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-950/30 dark:text-red-200"
                >
                  {modalError}
                </div>
              ) : null}

              <div className="grid gap-5 md:grid-cols-2">
                <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-800/40">
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white">
                        {t("reports.alumni.child_section", {
                          defaultValue: "Child details",
                        })}
                      </h3>
                      <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                        {t(
                          "reports.alumni.child_section_description",
                          {
                            defaultValue:
                              "Optional details stored with the alumni preference.",
                          }
                        )}
                      </p>
                    </div>

                    {showChildSection ? (
                      <button
                        type="button"
                        onClick={() => {
                          setShowChildSection(false);
                          setEditItem((previous) =>
                            previous
                              ? {
                                  ...previous,
                                  childId: "",
                                  child: {
                                    allowStore: false,
                                    name: "",
                                    dateBirth: "",
                                    email: "",
                                  },
                                }
                              : previous
                          );
                        }}
                        className="shrink-0 text-xs font-semibold text-red-600 hover:text-red-700 dark:text-red-300"
                      >
                        {t("common.actions.remove", {
                          defaultValue: "Remove",
                        })}
                      </button>
                    ) : null}
                  </div>

                  {showChildSection ? (
                    <div className="space-y-4">
                      <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-sm font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                        <input
                          type="checkbox"
                          checked={editItem.child.allowStore}
                          onChange={(event) =>
                            updateChildField(
                              "allowStore",
                              event.target.checked
                            )
                          }
                          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        {t("reports.alumni.allow_child", {
                          defaultValue: "Store child details",
                        })}
                      </label>

                      <label className="block">
                        <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {t("reports.alumni.child_name", {
                            defaultValue: "Child name",
                          })}
                        </span>
                        <input
                          type="text"
                          value={editItem.child.name}
                          onChange={(event) =>
                            updateChildField(
                              "name",
                              event.target.value
                            )
                          }
                          className="min-h-11 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-950"
                        />
                      </label>

                      <label className="block">
                        <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {t("reports.alumni.date_of_birth", {
                            defaultValue: "Date of birth",
                          })}
                        </span>
                        <input
                          type="date"
                          value={editItem.child.dateBirth}
                          onChange={(event) =>
                            updateChildField(
                              "dateBirth",
                              event.target.value
                            )
                          }
                          className="min-h-11 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-950"
                        />
                      </label>

                      <label className="block">
                        <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {t("reports.alumni.child_email", {
                            defaultValue: "Child email",
                          })}
                        </span>
                        <input
                          type="email"
                          value={editItem.child.email}
                          onChange={(event) =>
                            updateChildField(
                              "email",
                              event.target.value
                            )
                          }
                          className="min-h-11 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-950"
                        />
                      </label>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setShowChildSection(true);
                        updateChildField("allowStore", true);
                      }}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-5 text-sm font-semibold text-slate-600 transition hover:border-indigo-300 hover:text-indigo-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-indigo-500 dark:hover:text-indigo-200"
                    >
                      <FaPlus aria-hidden="true" className="h-3.5 w-3.5" />
                      {t("reports.alumni.add_child", {
                        defaultValue: "Add child details",
                      })}
                    </button>
                  )}
                </section>

                <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-800/40">
                  <div className="mb-4">
                    <h3 className="font-bold text-slate-900 dark:text-white">
                      {t("reports.alumni.parent_section", {
                        defaultValue: "Parent contact",
                      })}
                    </h3>
                    <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                      {t(
                        "reports.alumni.parent_section_description",
                        {
                          defaultValue:
                            "Contact details and communication preferences.",
                        }
                      )}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {t("reports.alumni.parent_name", {
                          defaultValue: "Parent name",
                        })}
                      </span>
                      <input
                        type="text"
                        value={editItem.parents.name}
                        onChange={(event) =>
                          updateParentField(
                            "name",
                            event.target.value
                          )
                        }
                        className="min-h-11 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-950"
                      />
                    </label>

                    <label className="block">
                      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {t("reports.alumni.parent_email", {
                          defaultValue: "Parent email",
                        })}
                      </span>
                      <input
                        type="email"
                        value={editItem.parents.email}
                        onChange={(event) =>
                          updateParentField(
                            "email",
                            event.target.value
                          )
                        }
                        className="min-h-11 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-950"
                      />
                    </label>

                    <fieldset className="space-y-2">
                      <legend className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {t(
                          "reports.alumni.communication_preferences",
                          {
                            defaultValue:
                              "Communication preferences",
                          }
                        )}
                      </legend>

                      {(
                        [
                          [
                            "newsletter",
                            t("parentPortal.alumni.newsletter", {
                              defaultValue: "Newsletter",
                            }),
                          ],
                          [
                            "alumni",
                            t("parentPortal.alumni.alumni", {
                              defaultValue: "Alumni updates",
                            }),
                          ],
                          [
                            "activities",
                            t("parentPortal.alumni.activities", {
                              defaultValue: "Activities",
                            }),
                          ],
                        ] as const
                      ).map(([field, label]) => (
                        <label
                          key={field}
                          className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-sm font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                        >
                          <input
                            type="checkbox"
                            checked={editItem.parents[field]}
                            onChange={(event) =>
                              updateParentField(
                                field,
                                event.target.checked
                              )
                            }
                            className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          {label}
                        </label>
                      ))}
                    </fieldset>
                  </div>
                </section>
              </div>
            </div>

            <footer className="sticky bottom-0 flex flex-col-reverse gap-3 border-t border-slate-200 bg-white/95 px-5 py-4 backdrop-blur dark:border-slate-700 dark:bg-slate-900/95 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div>
                {canDelete && !editItem.isNew && !editItem.lifecycleOnly ? (
                  <button
                    type="button"
                    onClick={() => void handleDelete()}
                    disabled={modalSaving || !canDelete}
                    className="inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-500/30 dark:bg-red-950/30 dark:text-red-200 dark:hover:bg-red-950/50 sm:w-auto"
                  >
                    {t("common.actions.delete", {
                      defaultValue: "Delete",
                    })}
                  </button>
                ) : null}
              </div>

              <div className="flex flex-col-reverse gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={modalSaving}
                  className="inline-flex min-h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  {t("common.actions.cancel", {
                    defaultValue: "Cancel",
                  })}
                </button>

                {canManageAlumni ? (
                  <button
                    type="button"
                    onClick={() => void handleSave()}
                    disabled={modalSaving}
                    className="inline-flex min-h-11 items-center justify-center rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {modalSaving
                      ? t("common.loading", {
                          defaultValue: "Saving...",
                        })
                      : t("common.actions.save", {
                          defaultValue: "Save",
                        })}
                  </button>
                ) : null}
              </div>
            </footer>
          </section>
        </div>
      ) : null}
    </PageTemplate>
  );
};

export default ReportsAlumni;
